/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailContext.js
 * @description
 * Cross-cutting state for the in-app mail client: mailbox settings, live status
 * (unread / queue / quota / configured flag), folders + labels, and the active
 * account. Individual screens own their own paginated message lists (fetched via
 * {@link module:services/mailApi}); this provider holds the shared, app-wide bits
 * that several screens and the universal "Mail" menu badge all read.
 *
 * Mutation helpers (mark read, star, move, label, send, …) call the API and then
 * refresh status so the unread badge stays correct everywhere, and bump a
 * `mutationTick` so any visible list can reconcile itself.
 *
 * Mounted once near the top of the tree in AppProviders so the unread badge is
 * available in every app mode (Sales / Service / Logistics / Management).
 *
 * NEVER blocks first paint: `children` render synchronously and the whole
 * bootstrap (settings/status/folders, poll loop, socket) is deferred behind
 * `InteractionManager.runAfterInteractions` and gated on `configured`.
 *
 * It also feeds the module-level reader cache (`helpers/mailCache`): incoming
 * mail notifications prime a stub + prefetch the body on RECEIPT, mutations keep
 * cached copies coherent, and every cache helper is re-exported through
 * `useMail()` so screens can prime/prefetch without a second import.
 */

import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {AppState, InteractionManager} from 'react-native';
import * as Notifications from 'expo-notifications';
import {io as createSocketClient} from 'socket.io-client';
import * as mailApi from '../services/mailApi';
import {
    cacheMailThread,
    clearMailCache,
    getCachedMailMessage,
    getCachedMailThread,
    prefetchMailMessage,
    prefetchMailThread,
    primeMailMessage,
    primeMailMessages,
    primeMailStubFromPush,
} from './mailCache';
import {getAccessToken, getUserInfo} from './storage';
import {getSetting, setSetting} from './persistentSettings';
import {refreshAppBadge} from './appBadge';
import {MAIL_POLL_DEFAULT_MS, MAIL_POLL_INTERVAL_KEY, sanitizeMailPollMs} from './mailPreferences';
import {ApiConfig} from '../config/ApiConfig';
import {
    applySnoozeDueEvent,
    applyThreadChatEvent,
    applyThreadStateEvent,
    bootstrap as bootstrapCollab,
    refreshAll as refreshCollab,
    resetCollabStore,
} from './mailCollabStore';
import {isCollabEvent} from './mailCollabUtils';

const MailContext = createContext(null);

// Minimum spacing between background `POST /mail/sync` IMAP pulls triggered by the foreground
// poll loop. The poll tick runs every ~20s but an IMAP fetch is far heavier, so we coalesce to
// at most once a minute; pull-to-refresh (`syncNow`) and app-foreground bypass this throttle.
const BACKGROUND_IMAP_SYNC_MIN_MS = 60 * 1000;

const STANDARD_FOLDERS = [
    {key: 'INBOX', icon: 'inbox', system: true},
    {key: 'Sent', icon: 'send', system: true},
    {key: 'Drafts', icon: 'file-document-edit-outline', system: true},
    {key: 'Archive', icon: 'archive-outline', system: true},
    {key: 'Spam', icon: 'alert-octagon-outline', system: true},
    {key: 'Trash', icon: 'trash-can-outline', system: true},
];

/**
 * Safe array coercion used throughout — backend lists may be undefined.
 * @param {*} value @returns {Array}
 */
const arr = (value) => (Array.isArray(value) ? value : []);

/**
 * Fire-and-forget: mirror the freshly synced mailbox into the home-screen
 * widget snapshot (unread count + latest inbox rows) via the light mail-only
 * publish path (`widgetRefresh.refreshMailWidgetData`). Lazily required so a
 * build without the widget pipeline (web, Expo Go) degrades to a silent no-op,
 * and never awaited — a widget write must never slow down or fail the mail
 * flow itself.
 * @returns {void}
 */
const publishMailWidgetData = () => {
    try {
        // eslint-disable-next-line global-require
        const {refreshMailWidgetData} = require('./widgets/widgetRefresh');
        refreshMailWidgetData().catch(() => {});
    } catch (_) {
        // Widget pipeline absent — ignore.
    }
};

export const MailProvider = ({children}) => {
    const [settings, setSettings] = useState(null);
    const [status, setStatus] = useState(null);
    const [folders, setFolders] = useState([]);
    // Custom-folder definitions `{id, name, color, system}` (items[].label is only the id).
    const [directories, setDirectories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [mutationTick, setMutationTick] = useState(0);
    const [activeAccountId, setActiveAccountId] = useState(null);
    const [pollIntervalMs, setPollIntervalMsState] = useState(MAIL_POLL_DEFAULT_MS);
    const mountedRef = useRef(true);
    const loadedOnceRef = useRef(false);
    // Live (foreground) polling plumbing — see the polling effect below.
    const pollTimerRef = useRef(null);
    const pollIntervalRef = useRef(MAIL_POLL_DEFAULT_MS);
    const appActiveRef = useRef(true);
    const lastSnapshotRef = useRef('');
    // Throttles the background IMAP pull so the foreground poll never hammers the mail server:
    // at most one `POST /mail/sync` per BACKGROUND_IMAP_SYNC_MIN_MS regardless of tick cadence.
    const lastBackgroundSyncRef = useRef(0);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    const configured = Boolean(status?.configured ?? settings?.configured);
    // Backend `getMailboxStatus` shape: `inbox.unreadIncomingCount`,
    // `queue.queuedOutgoingCount`, `queue.failedOutgoingCount` (legacy short
    // keys kept as fallbacks).
    const unreadCount = Number(status?.inbox?.unreadIncomingCount ?? status?.inbox?.unreadCount ?? 0);
    const queuedCount = Number(status?.queue?.queuedOutgoingCount ?? status?.queue?.queuedCount ?? 0);
    const failedCount = Number(status?.queue?.failedOutgoingCount ?? status?.queue?.failedCount ?? 0);

    const accounts = useMemo(() => arr(settings?.accounts), [settings]);
    const labels = useMemo(() => arr(settings?.labels), [settings]);

    /** Bump the mutation counter so visible lists re-fetch themselves. */
    const invalidate = useCallback(() => {
        if (mountedRef.current) {
            setMutationTick((tick) => tick + 1);
        }
    }, []);

    /** Refresh the lightweight status summary (unread/queue/quota). */
    const refreshStatus = useCallback(async () => {
        try {
            const token = await getAccessToken();
            if (!token) {
                return null;
            }
            const next = await mailApi.getMailStatus();
            if (mountedRef.current) {
                setStatus(next);
            }
            // The mailbox unread count just moved — keep the app icon badge (unread
            // notifications + unread mail) in step. The helper coalesces concurrent
            // callers and only writes the icon when the number actually changed.
            refreshAppBadge();

            return next;
        } catch (e) {
            // Status is best-effort; never surface as a blocking error.
            return null;
        }
    }, []);

    /** Refresh folders (with unread counts) merged over the standard set. */
    const refreshFolders = useCallback(async () => {
        try {
            const result = await mailApi.listMailFolders();
            // Backend `getMailboxFolders` returns `{items, labels, directories}`.
            const list = arr(result?.items || result?.folders || result);
            if (mountedRef.current) {
                setFolders(list);
                setDirectories(arr(result?.directories));
            }
            return list;
        } catch (e) {
            return [];
        }
    }, []);

    /** Refresh full mailbox settings (accounts, labels, signatures, prefs). */
    const refreshSettings = useCallback(async () => {
        try {
            const next = await mailApi.getMailSettings();
            if (mountedRef.current) {
                setSettings(next);
                if (!activeAccountId) {
                    const def = arr(next?.accounts).find((a) => a.isDefault) || arr(next?.accounts)[0];
                    if (def) {
                        setActiveAccountId(def.id || def._id || null);
                    }
                }
            }
            return next;
        } catch (e) {
            if (mountedRef.current) {
                setError(e?.message || 'Failed to load mailbox settings');
            }
            return null;
        }
    }, [activeAccountId]);

    /** Full bootstrap load — settings + status + folders. */
    const refreshAll = useCallback(async ({silent = false} = {}) => {
        const token = await getAccessToken();
        if (!token) {
            return;
        }
        if (mountedRef.current) {
            if (silent) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);
        }
        try {
            await Promise.all([refreshSettings(), refreshStatus(), refreshFolders()]);
            loadedOnceRef.current = true;
            // Mirror the freshly loaded mailbox into the home-screen mail widget.
            publishMailWidgetData();
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [refreshSettings, refreshStatus, refreshFolders]);

    /**
     * Pull new mail from the server via IMAP in the background (`POST /mail/sync`), throttled
     * to at most once per {@link BACKGROUND_IMAP_SYNC_MIN_MS}. The backend detects most mail in
     * seconds via IMAP IDLE, but this guarantees the app fetches the latest even when IDLE is
     * unavailable/dropped for the account — so the list reflects the mailbox, not just what was
     * last ingested. Fire-and-forget safe: never throws.
     *
     * @param {{force?: boolean}} [opts] `force` bypasses the throttle (pull-to-refresh / foreground).
     * @returns {Promise<boolean>} Whether a sync was actually triggered this call.
     */
    const triggerBackgroundSync = useCallback(async ({force = false} = {}) => {
        if (!appActiveRef.current || !loadedOnceRef.current) return false;
        const now = Date.now();
        if (!force && now - lastBackgroundSyncRef.current < BACKGROUND_IMAP_SYNC_MIN_MS) return false;
        lastBackgroundSyncRef.current = now; // optimistic — block concurrent double-fire
        try {
            await mailApi.syncMailbox();

            return true;
        } catch (_) {
            return false;
        }
    }, []);

    /**
     * One foreground poll tick: pull new mail from IMAP (throttled), refresh the lightweight
     * status, and only when the unread/queue snapshot actually changed, invalidate visible
     * lists + folders so new mail shows up without a constant full re-fetch.
     */
    const pollTick = useCallback(async () => {
        if (!appActiveRef.current || !loadedOnceRef.current) return;
        await triggerBackgroundSync();
        const next = await refreshStatus();
        if (!next) return;
        const snapshot = JSON.stringify({
            u: next?.inbox?.unreadIncomingCount ?? next?.inbox?.unreadCount ?? 0,
            q: next?.queue?.queuedOutgoingCount ?? next?.queue?.queuedCount ?? 0,
            f: next?.queue?.failedOutgoingCount ?? next?.queue?.failedCount ?? 0,
        });
        if (snapshot !== lastSnapshotRef.current) {
            lastSnapshotRef.current = snapshot;
            invalidate();
            refreshFolders();
            // New/changed mail detected — mirror it into the home-screen widget.
            publishMailWidgetData();
        }
    }, [triggerBackgroundSync, refreshStatus, invalidate, refreshFolders]);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            globalThis.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        const ms = pollIntervalRef.current;
        if (!ms || ms <= 0) return; // "Off"
        pollTimerRef.current = globalThis.setInterval(() => { pollTick(); }, ms);
    }, [stopPolling, pollTick]);

    /** Change & persist the live (foreground) refresh cadence; `0` disables polling. */
    const setPollIntervalMs = useCallback((ms) => {
        const next = sanitizeMailPollMs(ms);
        setPollIntervalMsState(next);
        pollIntervalRef.current = next;
        setSetting(MAIL_POLL_INTERVAL_KEY, String(next)).catch(() => {});
        startPolling();
    }, [startPolling]);

    // Bootstrap: load settings/status/folders + the saved cadence, then start the
    // foreground poll loop. Pause it in the background and resume (with an immediate
    // refresh) on foreground — mirrors the admin web client's 20s inbox loop.
    useEffect(() => {
        mountedRef.current = true;
        // Mail bootstrap is DEFERRED off the first-paint critical path and STAGED so
        // a non-mail user (or any app mode that never opens mail) pays only ONE
        // lightweight status fetch (for the unread badge) — not the settings+folders
        // load, the foreground poll loop, or a forced IMAP pull. The heavy machinery
        // and the persistent Socket.IO (separate effect, gated on `configured`) only
        // spin up once the mailbox is actually configured.
        const task = InteractionManager.runAfterInteractions(() => {
            (async () => {
                const raw = await getSetting(MAIL_POLL_INTERVAL_KEY, null);
                const ms = sanitizeMailPollMs(raw);
                if (mountedRef.current) {
                    setPollIntervalMsState(ms);
                    pollIntervalRef.current = ms;
                }
                // Lightweight: populates `status` → unread badge + the `configured` flag.
                const st = await refreshStatus();
                if (mountedRef.current && st && st.configured === false) {
                    // Server DEFINITIVELY reports no mailbox: skip the folders/settings
                    // fetch, the poll loop and the forced IMAP pull. We only opt out on
                    // an explicit `configured === false` — if status is missing the flag
                    // or failed to load, fall through to the original full bootstrap so a
                    // configured (or briefly offline) user is never starved of mail.
                    return;
                }
                if (!mountedRef.current) {
                    return;
                }
                await refreshAll();
                startPolling();
                // Kick an immediate background IMAP pull on open so the inbox is current without
                // waiting a full poll interval; refresh the status/lists once it lands.
                triggerBackgroundSync({force: true}).then((did) => { if (did) pollTick(); });
            })();
        });
        const sub = AppState.addEventListener('change', (state) => {
            const active = state === 'active';
            const wasActive = appActiveRef.current;
            appActiveRef.current = active;
            if (active && !wasActive) {
                // Returning to the foreground: force an immediate IMAP pull, then refresh —
                // but only if the mailbox already bootstrapped (loadedOnceRef), so a
                // non-mail session never starts an (empty) poll loop.
                if (loadedOnceRef.current) {
                    triggerBackgroundSync({force: true}).finally(() => pollTick());
                    startPolling();
                }
            } else if (!active && wasActive) {
                stopPolling();
            }
        });
        return () => {
            task?.cancel?.();
            sub?.remove?.();
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Instant inbox: the backend now detects new mail in seconds via IMAP IDLE and fires a
    // `mail.received` push. When that push is delivered while the app is foregrounded, refresh
    // immediately instead of waiting for the next poll tick — making new mail appear as fast as
    // a professional client. The interval poll stays as the fallback.
    useEffect(() => {
        const sub = Notifications.addNotificationReceivedListener((notification) => {
            const data = notification?.request?.content?.data || {};
            const type = String(data.type || data.eventKey || '').toLowerCase();
            if (type === 'mail' || type.startsWith('mail.') || data.mailMessageId || data.threadKey) {
                // Shared-mailbox collaboration pushes (mail.assigned / reassigned / shared_reply /
                // snooze_due): refresh tags, snoozes, counts and the loaded thread states.
                if (isCollabEvent(data)) {
                    refreshCollab().catch(() => {});
                }
                // Warm the reader cache on RECEIPT (not just on tap): a stub for the
                // instant first paint + the real body in flight, so a tap on the
                // banner opens an already-loaded mail. Fire-and-forget, never throws.
                try {
                    primeMailStubFromPush(data);
                    const messageId = String(data.mailMessageId || data.mailmessageid || '').trim();
                    const key = String(data.threadKey || data.threadkey || '').trim();
                    if (messageId) {
                        prefetchMailMessage(messageId);
                    }
                    if (key) {
                        prefetchMailThread(key);
                    }
                } catch (_) {
                    // caching is best-effort
                }
                pollTick();
            }
        });
        return () => sub?.remove?.();
    }, [pollTick]);

    // Instant inbox via Socket.IO — the reliable real-time path. The backend detects new mail in
    // seconds through IMAP IDLE and emits `notification:new` (type `mail`) to the recipient's
    // personal room (`user:<id>`) the moment the message is ingested. Push (FCM/APNs) delivery is
    // unreliable — broken on web, best-effort on native — so a dedicated socket makes new mail
    // appear immediately regardless of push, keeping Happy Mailer as fast as (or faster than) any
    // native client. The 20 s foreground poll and the push listener above stay as fallbacks; a
    // socket-driven `pollTick()` is de-duped by its snapshot check, so an extra tick is harmless.
    useEffect(() => {
        // Do NOT hold a permanent Socket.IO connection (reconnectionAttempts:Infinity)
        // for a user whose mailbox isn't configured, or in an app mode that never
        // opens mail. The effect re-runs and connects the moment `configured` flips
        // true (after the staged bootstrap), and disconnects if it flips false.
        if (!configured) {
            return undefined;
        }

        let cancelled = false;
        let socket = null;

        (async () => {
            let token = null;
            let userId = null;
            try {
                token = await getAccessToken();
                userId = (await getUserInfo())?._id || null;
            } catch (_) {
                token = null;
            }
            if (cancelled || !userId) {
                return;
            }

            try {
                socket = createSocketClient(ApiConfig.apiURL, {
                    transports: ['websocket', 'polling'],
                    timeout: 5000,
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 3000,
                    auth: token ? {token, userId} : {userId},
                });

                // (Re)join the personal room on every (re)connect so a dropped socket keeps
                // receiving `notification:new` targeted at this user.
                const joinUserRoom = () => socket.emit('join:user', userId);
                socket.on('connect', joinUserRoom);
                socket.on('notification:new', (payload) => {
                    const type = String(payload?.type || payload?.metadata?.eventKey || '').toLowerCase();
                    if (type === 'mail' || type.startsWith('mail.')) {
                        pollTick();
                    }
                    if (isCollabEvent(payload?.metadata)) {
                        refreshCollab().catch(() => {});
                    }
                });
                // Shared-mailbox collaboration realtime (same user room / same socket): a thread's
                // assignee / tags / status / replied-by changed, or a snoozed conversation came due.
                socket.on('mail:thread_state', (payload) => {
                    applyThreadStateEvent(payload);
                });
                // Private thread discussion (shared mailboxes): badge + open sheet refetch. Body-less
                // payload `{address, conversationKey, messageId, authorId}`; same socket, no second one.
                socket.on('mail:thread_chat', (payload) => {
                    applyThreadChatEvent(payload);
                });
                socket.on('mail:snooze_due', () => {
                    applySnoozeDueEvent();
                    pollTick();
                });
                // Dedicated instant-mail event: the backend emits `mail:new` the moment a message
                // lands via IMAP IDLE (before any notification fan-out). Refresh immediately so new
                // mail appears in ~1-2s like a native mail client. pollTick is snapshot-de-duped,
                // so overlapping with notification:new is harmless.
                socket.on('mail:new', () => {
                    pollTick();
                });
                // Two-way sync: messages were read / starred / moved / deleted — in another mail
                // client (Gmail, Outlook…, `source: 'server'`) or in Happy Mailer on another device
                // (`source: 'local'`). Patch the reader cache, then refresh lists + counts in one
                // coalesced pass (no IMAP pull — the server already has the change).
                let updatedTimer = null;
                socket.on('mail:updated', (payload) => {
                    try {
                        arr(payload?.flags).forEach((change) => {
                            const key = String(change?.id || '');
                            if (key && getCachedMailMessage(key)) {
                                primeMailMessage({id: key, isRead: Boolean(change.seen), isStarred: Boolean(change.flagged)});
                            }
                        });
                        arr(payload?.moved).forEach((change) => {
                            const key = String(change?.id || '');
                            if (key && change?.folder && getCachedMailMessage(key)) {
                                primeMailMessage({id: key, folder: change.folder});
                            }
                        });
                    } catch (_) {
                        // cache coherence is best-effort
                    }
                    if (updatedTimer) {
                        globalThis.clearTimeout(updatedTimer);
                    }
                    updatedTimer = globalThis.setTimeout(() => {
                        updatedTimer = null;
                        if (cancelled) return;
                        refreshStatus().catch(() => {});
                        invalidate();
                        refreshFolders();
                    }, 300);
                });
            } catch (_) {
                // Best-effort — the poll loop + push listener remain as fallbacks.
            }
        })();

        return () => {
            cancelled = true;
            if (socket) {
                try {
                    socket.removeAllListeners();
                    socket.disconnect();
                } catch (_) {
                    // ignore teardown races
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollTick, configured]);

    // Collaboration store: bind to the signed-in user once the mailbox is configured; drop it when not.
    useEffect(() => {
        if (configured) {
            bootstrapCollab().catch(() => {});
        } else {
            resetCollabStore();
        }
    }, [configured]);

    /* ──────────────── Mutation helpers (refresh status + invalidate) ─────── */

    const afterMutation = useCallback(async () => {
        await refreshStatus();
        invalidate();
    }, [refreshStatus, invalidate]);

    /**
     * Keep the module-level reader cache ({@link module:helpers/mailCache}) in sync with a
     * flag/folder mutation, so a cached message can never re-open with a stale read/star/folder
     * state. Only messages ALREADY cached are patched — this never invents a body-less entry.
     * @param {Array<string>} ids Mutated message ids.
     * @param {Object} patch Fields to merge onto each cached message.
     * @returns {void}
     */
    const patchCachedMessages = useCallback((ids, patch) => {
        try {
            arr(ids).forEach((id) => {
                const key = String(id || '');
                if (key && getCachedMailMessage(key)) {
                    primeMailMessage({id: key, ...patch});
                }
            });
        } catch (_) {
            // cache coherence is best-effort, never a mutation failure
        }
    }, []);

    const markRead = useCallback(async (ids, isRead = true) => {
        const list = arr(ids);
        if (list.length === 1) {
            await mailApi.updateMailMessageFlags(list[0], {isRead});
        } else if (list.length > 1) {
            await mailApi.bulkUpdateMailMessageFlags({ids: list, isRead});
        }
        patchCachedMessages(list, {isRead});
        await afterMutation();
    }, [afterMutation, patchCachedMessages]);

    const starMessages = useCallback(async (ids, isStarred = true) => {
        const list = arr(ids);
        if (list.length === 1) {
            await mailApi.updateMailMessageFlags(list[0], {isStarred});
        } else if (list.length > 1) {
            await mailApi.bulkUpdateMailMessageFlags({ids: list, isStarred});
        }
        patchCachedMessages(list, {isStarred});
        await afterMutation();
    }, [afterMutation, patchCachedMessages]);

    const moveMessages = useCallback(async (ids, folder) => {
        const list = arr(ids);
        if (list.length === 1) {
            await mailApi.moveMailMessage(list[0], {folder});
        } else if (list.length > 1) {
            await mailApi.moveMailMessages({ids: list, folder});
        }
        patchCachedMessages(list, {folder});
        await afterMutation();
    }, [afterMutation, patchCachedMessages]);

    const archiveMessages = useCallback((ids) => moveMessages(ids, 'Archive'), [moveMessages]);
    const trashMessages = useCallback((ids) => moveMessages(ids, 'Trash'), [moveMessages]);

    const labelMessages = useCallback(async (payload) => {
        await mailApi.updateMailMessageLabels(payload);
        await afterMutation();
    }, [afterMutation]);

    const sendMessage = useCallback(async (payload) => {
        const result = await mailApi.queueMailMessage(payload);
        await afterMutation();
        return result;
    }, [afterMutation]);

    const cancelSend = useCallback(async (id) => {
        const result = await mailApi.cancelMailMessage(id);
        await afterMutation();
        return result;
    }, [afterMutation]);

    const syncNow = useCallback(async () => {
        try {
            await mailApi.syncMailbox();
        } finally {
            await afterMutation();
            await refreshFolders();
        }
    }, [afterMutation, refreshFolders]);

    const saveSettings = useCallback(async (payload) => {
        const next = await mailApi.updateMailSettings(payload);
        if (mountedRef.current && next) {
            setSettings(next);
        }
        await refreshStatus();
        return next;
    }, [refreshStatus]);

    /**
     * Soft-delete a mail account. The account and all of its emails are hidden server-side; adopt
     * the returned settings, then refresh status + folder counts and invalidate open message lists
     * so the removed account's mail disappears immediately.
     * @param {string} id Account id to remove.
     * @returns {Promise<Object>} The refreshed mailbox settings.
     */
    const deleteAccount = useCallback(async (id) => {
        const next = await mailApi.deleteMailAccount(id);
        if (mountedRef.current && next) {
            setSettings(next);
        }
        // Drop every cached body: they may belong to the mailbox that just went away.
        clearMailCache();
        await afterMutation();
        await refreshFolders();
        return next;
    }, [afterMutation, refreshFolders]);

    /** Apply an externally-observed status (e.g. from a push poll). */
    const applyStatus = useCallback((next) => {
        if (next && mountedRef.current) {
            setStatus(next);
        }
    }, []);

    const value = useMemo(() => ({
        // state
        settings,
        status,
        folders,
        directories,
        standardFolders: STANDARD_FOLDERS,
        accounts,
        labels,
        activeAccountId,
        configured,
        unreadCount,
        queuedCount,
        failedCount,
        loading,
        refreshing,
        error,
        mutationTick,
        pollIntervalMs,
        // setters
        setActiveAccountId,
        setPollIntervalMs,
        applyStatus,
        // loaders
        refreshAll,
        refreshStatus,
        refreshFolders,
        refreshSettings,
        invalidate,
        // mutations
        markRead,
        starMessages,
        moveMessages,
        archiveMessages,
        trashMessages,
        labelMessages,
        sendMessage,
        cancelSend,
        syncNow,
        saveSettings,
        deleteAccount,
        // reader cache passthroughs (module-level, see helpers/mailCache) — screens may
        // import them directly too; exposing them here keeps `useMail()` a one-stop shop
        // and lets a screen prime/prefetch without a second import.
        primeMessages: primeMailMessages,
        primeMessage: primeMailMessage,
        primeStubFromPush: primeMailStubFromPush,
        getCachedMessage: getCachedMailMessage,
        cacheThread: cacheMailThread,
        getCachedThread: getCachedMailThread,
        prefetchMessage: prefetchMailMessage,
        prefetchThread: prefetchMailThread,
        clearCache: clearMailCache,
        // raw api passthrough for screens
        api: mailApi,
    }), [
        settings, status, folders, directories, accounts, labels, activeAccountId, configured,
        unreadCount, queuedCount, failedCount, loading, refreshing, error, mutationTick, pollIntervalMs,
        applyStatus, setPollIntervalMs, refreshAll, refreshStatus, refreshFolders, refreshSettings, invalidate,
        markRead, starMessages, moveMessages, archiveMessages, trashMessages, labelMessages,
        sendMessage, cancelSend, syncNow, saveSettings, deleteAccount,
    ]);

    return <MailContext.Provider value={value}>{children}</MailContext.Provider>;
};

/**
 * Access the mail context. Returns a stable fallback when used outside the
 * provider so the universal menu badge never crashes a non-mail screen.
 * @returns {Object}
 */
export const useMail = () => {
    const ctx = useContext(MailContext);
    if (!ctx) {
        return {
            settings: null, status: null, folders: [], directories: [], standardFolders: STANDARD_FOLDERS,
            accounts: [], labels: [], activeAccountId: null, configured: false,
            unreadCount: 0, queuedCount: 0, failedCount: 0, loading: false, refreshing: false,
            error: null, mutationTick: 0, pollIntervalMs: MAIL_POLL_DEFAULT_MS,
            setActiveAccountId: () => {}, setPollIntervalMs: () => {}, applyStatus: () => {},
            refreshAll: async () => {}, refreshStatus: async () => {}, refreshFolders: async () => {},
            refreshSettings: async () => {}, invalidate: () => {},
            markRead: async () => {}, starMessages: async () => {}, moveMessages: async () => {},
            archiveMessages: async () => {}, trashMessages: async () => {}, labelMessages: async () => {},
            sendMessage: async () => {}, cancelSend: async () => {}, syncNow: async () => {},
            saveSettings: async () => {}, deleteAccount: async () => {},
            primeMessages: primeMailMessages, primeMessage: primeMailMessage,
            primeStubFromPush: primeMailStubFromPush, getCachedMessage: getCachedMailMessage,
            cacheThread: cacheMailThread, getCachedThread: getCachedMailThread,
            prefetchMessage: prefetchMailMessage, prefetchThread: prefetchMailThread,
            clearCache: clearMailCache, api: mailApi,
        };
    }
    return ctx;
};

export default MailProvider;
